import React, { useState, useEffect } from 'react';
import { useRef } from "react";
import { useReactToPrint } from "react-to-print";
import UniversalInvoicePrint from "./UniversalInvoicePrint";
import './Purchases.css';
import api from '../Service/api';
import { useNavigate } from "react-router-dom";

function FactoryUse() {
  const [invoiceNumber, setInvoiceNumber] = useState(0); 
  const [customerName, setCustomerName] = useState("");  
  const [customerList, setCustomerList] = useState([]);
  const [description, setDescription] = useState("");
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [loadingVendors, setLoadingVendors] = useState(true);
  const [vendorList, setVendorList] = useState([]);
  const [selectedCustomerBalance, setSelectedCustomerBalance] = useState(null);
  const [searchTerms, setSearchTerms] = useState({});
  const [needsScroll, setNeedsScroll] = useState(false);
  const [sizeFilters, setSizeFilters] = useState({});
  
  const isInitialLoading = loadingProducts || loadingCategories || loadingVendors;
  
  const [invoiceDate, setInvoiceDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });
  
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  
  const [products, setProducts] = useState([{
    productName: '', category: '', quantity: '', price: '',
    size: '', ktae: '', gram: '', boxSize: '', particular: '', length: ''
  }]);
  const [productList, setProductList] = useState([]);
  const [categoryList, setCategoryList] = useState([]);
  const [printInvoice, setPrintInvoice] = useState(null);
  const printRef = useRef();
  const [canPrint, setCanPrint] = useState(false);
  const [loggedInUser, setLoggedInUser] = useState(null);

  useEffect(() => {
    const phone = localStorage.getItem("userPhone");
    if (!phone) return;
    api.get(`/authentication/userData/${phone}`)
      .then(res => setLoggedInUser(res.data.additionalDetail))
      .catch(err => console.error("USER DATA API ERROR:", err));
  }, []);

  useEffect(() => {
    const checkScroll = () => {
      const table = document.querySelector('.purchase-table');
      if (table) {
        const needsHorizontalScroll = table.scrollWidth > table.clientWidth;
        setNeedsScroll(needsHorizontalScroll);
      }
    };
    checkScroll();
    window.addEventListener('resize', checkScroll);
    return () => window.removeEventListener('resize', checkScroll);
  }, [products]);

  // Fetch vendors
  useEffect(() => {
    setLoadingVendors(true);
    api.get("/product/getAll")
      .then(res => {
        if (res.data.code === 1 && Array.isArray(res.data.additionalDetail)) {
          setVendorList(res.data.additionalDetail);
        }
      })
      .catch(err => console.error("Failed to fetch vendors", err))
      .finally(() => setLoadingVendors(false));
  }, []);
  
  const handleCustomerChange = (e) => {
    const name = e.target.value;
    setCustomerName(name);
    const customer = customerList.find(c => c.customerName === name);
    if (customer && customer.currentBalance !== undefined) {
      setSelectedCustomerBalance(customer.currentBalance);
    } else {
      setSelectedCustomerBalance(null);
    }
  };

  const getFilteredProductsForRow = (rowIndex, searchTerm, sizeFilter) => {
    let filtered = [...productList];
    
    const sizeStr = sizeFilter ? String(sizeFilter).trim() : '';
    
    if (sizeStr !== '') {
      const sizeNum = parseFloat(sizeStr);
      if (!isNaN(sizeNum)) {
        filtered = filtered.filter(p => {
          const cat = (p.category || "").toLowerCase();
          if (cat === 'roll') {
            return p.size && parseFloat(p.size) === sizeNum;
          }
          if (cat === 'reel' || cat === 'tota') {
            if (p.stockByWeight) {
              return Object.keys(p.stockByWeight).some(key => {
                const match = key.match(/^(\d+(?:\.\d+)?)/);
                return match && parseFloat(match[1]) === sizeNum;
              });
            }
            return false;
          }
          if (cat === 'packet') {
            return p.size && parseFloat(p.size) === sizeNum;
          }
          return true;
        });
      }
    }
    
    if (searchTerm && searchTerm.trim() !== '') {
      const term = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(p => 
        p.productName.toLowerCase().includes(term)
      );
    }
    
    return filtered.slice(0, 25);
  };

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: `Factory-Use-Invoice-${invoiceNumber}`,
  });

  const handlePrintClick = () => {
    if (!printInvoice) {
      alert("No invoice data to print");
      return;
    }
    handlePrint();
  };

  // Fetch products
  useEffect(() => {
    setLoadingProducts(true);
    api.get("/product/names-prices")
      .then(res => { 
        if (res.data.code === 1) {
          setProductList(res.data.additionalDetail);
        }
      })
      .catch(err => console.error(err))
      .finally(() => setLoadingProducts(false));
  }, []);

  // Fetch categories
  useEffect(() => {
    setLoadingCategories(true);
    api.get("/api/category/getCategories")
      .then(res => { if (res.data.code === 1) setCategoryList(res.data.additionalDetail); })
      .catch(err => console.error(err))
      .finally(() => setLoadingCategories(false));
  }, []);

  // Fetch customers
  useEffect(() => {
    api.get("/product/getAllCustomers")
      .then(res => { if (res.data.code === 1) setCustomerList(res.data.additionalDetail); })
      .catch(err => console.error(err));
  }, []);

  const refreshCustomerList = async () => {
    try {
      const res = await api.get("/product/getAllCustomers");
      if (res.data.code === 1) {
        setCustomerList(res.data.additionalDetail);
        console.log("✅ Customer list refreshed with latest balances");
      }
    } catch (err) {
      console.error("Error refreshing customer list", err);
    }
  };

  const totalQuantity = products.reduce((sum, p) => {
    const qty = parseFloat(p.quantity);
    return sum + (isNaN(qty) ? 0 : qty);
  }, 0);

  const refreshProductList = async () => {
    try {
      const res = await api.get("/product/names-prices");
      if (res.data.code === 1) setProductList(res.data.additionalDetail);
    } catch (err) {
      console.error("Error refreshing product list", err);
    }
  };

  const validateVendorForReelTotaRoll = () => {
    for (let i = 0; i < products.length; i++) {
      const p = products[i];
      const category = (p.category || "").toLowerCase();
      if ((category === "reel" || category === "tota") && (!p.particular || p.particular.trim() === "")) {
        alert(`Please enter the Vendor Name for ${p.productName} (Row ${i + 1})`);
        return false;
      }
    }
    return true;
  };
  const removeRow = (index) => {
  // Remove the product
  setProducts(prev => prev.filter((_, i) => i !== index));

  // Re‑index searchTerms and sizeFilters to keep them aligned
  const newSearchTerms = {};
  const newSizeFilters = {};

  Object.keys(searchTerms).forEach(key => {
    const k = parseInt(key, 10);
    if (k < index) newSearchTerms[k] = searchTerms[k];
    else if (k > index) newSearchTerms[k - 1] = searchTerms[k];
  });

  Object.keys(sizeFilters).forEach(key => {
    const k = parseInt(key, 10);
    if (k < index) newSizeFilters[k] = sizeFilters[k];
    else if (k > index) newSizeFilters[k - 1] = sizeFilters[k];
  });

  setSearchTerms(newSearchTerms);
  setSizeFilters(newSizeFilters);
};

  const handleChange = (index, field, value) => {
    const newProducts = [...products];
    newProducts[index][field] = value;

    if (field === "size") {
      setSizeFilters(prev => ({
        ...prev,
        [index]: value !== undefined && value !== null ? String(value) : ''
      }));
    }

    if (field === "length") {
      setSizeFilters(prev => ({
        ...prev,
        [index]: value !== undefined && value !== null ? String(value) : ''
      }));
    }

    if (field === "productName") {
      setSearchTerms(prev => ({
        ...prev,
        [index]: value
      }));
      const foundProduct = productList.find(p => p.productName.toLowerCase() === value.toLowerCase());
      if (foundProduct) {
        newProducts[index].price = foundProduct.sellPrice;
        newProducts[index].category = foundProduct.category;
        const categoryName = (foundProduct.category || "").toLowerCase();
        if (categoryName === 'roll' && foundProduct.size) {
          newProducts[index].size = foundProduct.size;
        }
      }
    }

    // const category = (newProducts[index].category || "").toLowerCase();
    // if (field === "quantity" && (category === "reel" || category === "tota" || category === "roll")) {
    //   const weightValue = parseFloat(value) || 0;
    //   newProducts[index].size = weightValue;
    // }

    if (field === "category") {
      const foundCategory = categoryList.find(c => c.categoryName.toLowerCase() === value.toLowerCase());
      newProducts[index].category = foundCategory ? foundCategory.categoryName : value;
    }

    if (field === "category") {
      const newCategory = value.toLowerCase();
      if (newCategory !== 'box') newProducts[index].boxSize = '';
      if (newCategory !== 'packet') { newProducts[index].ktae = ''; newProducts[index].gram = ''; }
      if (!['packet', 'roll', 'reel', 'tota', 'box'].includes(newCategory)) newProducts[index].size = '';
      if (newCategory !== 'reel' && newCategory !== 'tota') {
        newProducts[index].length = '';
      }
    }

    const currentCategory = (newProducts[index].category || "").toLowerCase();
    const price = parseFloat(newProducts[index].price) || 0;
    const quantity = parseFloat(newProducts[index].quantity) || 0;
    const size = parseFloat(newProducts[index].size) || 0;
    const ktae = parseFloat(newProducts[index].ktae) || 0;
    const gram = parseFloat(newProducts[index].gram) || 0;

    if (currentCategory === 'packet') {
      newProducts[index].calculatedTotal = ((quantity * size * ktae * gram * price) / 15500).toFixed(2);
    } else if (currentCategory === 'roll') {
      newProducts[index].calculatedTotal = (price * quantity * size).toFixed(2);
    } else if (currentCategory === 'reel' || currentCategory === 'tota') {
      newProducts[index].calculatedTotal = (price * quantity).toFixed(2);
    } else {
      newProducts[index].calculatedTotal = (price * quantity).toFixed(2);
    }

    setProducts(newProducts);
  };

  const addRow = () => {
    setProducts([...products, { 
      productName: '', category: '', quantity: '', price: '', 
      size: '', ktae: '', gram: '', boxSize: '', particular: '', length: ''
    }]);
  };

  const removeAllRows = () => setProducts([]);

  const renderStockDisplay = (selectedProduct, productName, category) => {
    if (!selectedProduct || !productName) return null;
    
    const cat = (selectedProduct.category || "").toLowerCase();
    const isReelTota = cat === 'reel' || cat === 'tota';
    const isRollCat = cat === 'roll';
    const isPacketCat = cat === 'packet';
    
    if ((isReelTota || isRollCat) && selectedProduct.stockByWeight && Object.keys(selectedProduct.stockByWeight).length > 0) {
      const stockEntries = Object.entries(selectedProduct.stockByWeight).filter(([_, qty]) => qty > 0);
      if (stockEntries.length === 0) {
        return <div style={{ color: '#ff4444', backgroundColor: '#ffebee', padding: '6px 8px', borderRadius: '4px', marginTop: '4px', border: '1px solid #ff4444', fontSize: '11px' }}><strong>⚠️ Out of Stock</strong></div>;
      }
      return (
        <div style={{ color: '#0a8938', backgroundColor: '#f8f9fa', padding: '6px 8px', borderRadius: '4px', marginTop: '4px', border: '1px solid #0a8938', fontSize: '11px' }}>
          <strong style={{ fontSize: '10px', display: 'block', marginBottom: '4px' }}>📊 Available Stock:</strong>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {stockEntries.map(([display, qty]) => (
              <span key={display} style={{ backgroundColor: '#e8f5e9', padding: '2px 6px', borderRadius: '12px', fontSize: '10px', fontWeight: 'bold' }}>
                {display}: <span style={{ color: '#2e7d32' }}>{qty}</span>
              </span>
            ))}
          </div>
        </div>
      );
    } else if (isPacketCat && selectedProduct.packetStockByVariant && Object.keys(selectedProduct.packetStockByVariant).length > 0) {
      const totalStock = selectedProduct.stockQuantity || 0;
      const variantEntries = Object.entries(selectedProduct.packetStockByVariant).filter(([_, qty]) => qty > 0);
      if (totalStock <= 0) {
        return <div style={{ color: '#ff4444', backgroundColor: '#ffebee', padding: '6px 8px', borderRadius: '4px', marginTop: '4px', border: '1px solid #ff4444', fontSize: '11px' }}><strong>⚠️ Out of Stock</strong></div>;
      }
      return (
        <div style={{ color: '#0a8938', backgroundColor: '#f8f9fa', padding: '6px 8px', borderRadius: '4px', marginTop: '4px', border: '1px solid #0a8938', fontSize: '11px' }}>
          <div><strong>📦 Total Stock: {totalStock}</strong></div>
          {variantEntries.length > 0 && (
            <>
              <div style={{ fontSize: '10px', marginTop: '4px', color: '#555' }}>Breakdown by variant:</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '2px' }}>
                {variantEntries.map(([variant, qty]) => (
                  <span key={variant} style={{ backgroundColor: '#e8f5e9', padding: '2px 6px', borderRadius: '12px', fontSize: '9px', fontWeight: 'bold' }}>
                    {variant}: {qty}
                  </span>
                ))}
              </div>
            </>
          )}
        </div>
      );
    } else if (selectedProduct.stockQuantity !== undefined && selectedProduct.stockQuantity > 0) {
      return <div style={{ fontSize: '11px', color: '#0a8938', marginTop: '2px' }}>📦 In Stock: {selectedProduct.stockQuantity}</div>;
    } else if (selectedProduct.stockQuantity !== undefined && selectedProduct.stockQuantity <= 0) {
      return <div style={{ fontSize: '11px', color: '#ff4444', marginTop: '2px' }}>📦 Out of Stock</div>;
    }
    return null;
  };

  const handleSubmit = async () => {
    if (isLoading) return;
    if (!customerName) { alert("Please enter customer name"); return; }
    if (products.length === 0 || !products[0].productName) { alert("Please add at least one product"); return; }

    for (let i = 0; i < products.length; i++) {
      const p = products[i];
      const category = (p.category || "").toLowerCase();
      if ((category === 'reel' || category === 'tota' || category === 'roll') && (!p.quantity || parseFloat(p.quantity) <= 0)) {
        alert(`${category === 'roll' ? 'Quantity' : 'Weight (Quantity)'} is required for ${category} product: ${p.productName} (Row ${i + 1})`);
        return;
      }
      if ((category === 'reel' || category === 'tota') && (!p.length || parseFloat(p.length) <= 0)) {
        alert(`Length is required for ${category} product: ${p.productName} (Row ${i + 1})`);
        return;
      }
    }
    if (!validateVendorForReelTotaRoll()) return;

    setIsLoading(true);
    try {
      const requestBody = {
        products: products.map(p => {
          const category = p.category.toLowerCase();
          const baseProduct = {
            productName: p.productName,
            category: p.category,
            quantity: parseFloat(p.quantity) || 0,
            price: parseFloat(p.price) || 0,
            particular: p.particular || "",
            length: 0
          };
          if (category === 'box') {
            return { ...baseProduct, size: 0, boxSize: p.boxSize || "", ktae: 0, gram: 0 };
          } else if (category === 'packet') {
            return { ...baseProduct, size: parseFloat(p.size) || 0, ktae: parseFloat(p.ktae) || 0, gram: parseFloat(p.gram) || 0, boxSize: null };
          } else if (category === 'roll') {
            return { ...baseProduct, size: parseFloat(p.size) || 0, ktae: 0, gram: 0, boxSize: null };
          } else if (category === 'reel' || category === 'tota') {
            return { 
              ...baseProduct, 
              size: parseFloat(p.quantity) || 0, 
              length: parseFloat(p.length) || 0,
              ktae: null,
              gram: null, 
              boxSize: null 
            };
          } else {
            return { ...baseProduct, size: 0, ktae: null, gram: null, boxSize: null };
          }
        }),
        description: description,
        customerName: customerName,
        status: "FactoryUse",
        invoiceDate: invoiceDate
      };

      const response = await api.post("/factory/factory-use", requestBody);

      const generatedInvoiceNumber = response.data.returnId || invoiceNumber || 0;
      console.log("📄 Generated Factory Use Invoice Number:", generatedInvoiceNumber);
      
      setInvoiceNumber(generatedInvoiceNumber);
      alert(response.data.message || "✅ Factory use saved successfully!");

      setCanPrint(true);
      setPrintInvoice({
        invoiceNumber: generatedInvoiceNumber,
        customerName,
        products: JSON.parse(JSON.stringify(products)),
        invoiceDiscount: 0,
        invoiceRent: 0,
        payBill: 0,
        gstPercentage: 0,
        grandTotal: grandTotal,
        user: loggedInUser,
        invoiceDate: invoiceDate,
        description: description
      });

      // Reset form fields
      setProducts([{ productName: '', category: '', quantity: '', price: '', size: '', ktae: '', gram: '', boxSize: '', particular: '', length: '' }]);
      setCustomerName("");
      setSelectedCustomerBalance(null);
      setDescription("");
      setInvoiceDate(new Date().toISOString().split('T')[0]);
      
      await refreshProductList();
      await refreshCustomerList();
      
    } catch (error) {
      console.error(error);
      alert("❌ Failed to save factory use. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  let grandTotal = products.reduce((sum, p) => sum + (parseFloat(p.calculatedTotal) || 0), 0);
  grandTotal = grandTotal.toFixed(2);

  const categories = products.map(p => (p.category || "").toLowerCase());
  const showPacketColumns = categories.includes("packet");
  const showRollColumns = categories.some(c => ["roll"].includes(c));
  const showBoxColumns = categories.includes("box");
  const showParticularColumn = true;
  const showWeightLabel = products.some(p => { const cat = (p.category || "").toLowerCase(); return cat === "reel" || cat === "tota" || cat === "roll"; });
  const hasReelOrTotaOrRoll = products.some(p => { const cat = (p.category || "").toLowerCase(); return cat === "reel" || cat === "tota"; });
  const showLengthColumn = products.some(p => { const cat = (p.category || "").toLowerCase(); return cat === "reel" || cat === "tota"; });
  const showSizeColumn = showPacketColumns || showRollColumns || showLengthColumn;

  if (isInitialLoading) {
    return (
      <div className="purchase-table-container" style={{ textAlign: 'center', padding: '50px' }}>
        <div style={{ fontSize: '18px', color: '#0a8938' }}>⏳ Loading data... Please wait.</div>
        {loadingProducts && <div>📦 Fetching products...</div>}
        {loadingCategories && <div>📂 Fetching categories...</div>}
        {loadingVendors && <div>🏢 Fetching vendors...</div>}
      </div>
    );
  }

  return (
    <div className={`purchase-table-container ${needsScroll ? 'has-scroll' : ''}`}>
      <h2 className="page-title" style={{ color: "#0a8938" }}>Factory Use Details</h2>

      {needsScroll && (
        <div className="scroll-hint">↔ Scroll horizontally to see all columns</div>
      )}

      {/* Top Form */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "20px", marginBottom: "20px" }}>
        <div><label style={{ fontWeight: "bold" }}>Invoice #</label><input type="text" value={invoiceNumber || "Auto-generated"} readOnly className="read-only-input" disabled={isLoading} /></div>
        <div><label style={{ fontWeight: "bold" }}>Invoice Date</label><input type="date" value={invoiceDate} onChange={e => setInvoiceDate(e.target.value)} className="read-only-input" max={new Date().toISOString().split('T')[0]} disabled={isLoading} /></div>
        <div>
          <label style={{ fontWeight: "bold" }}>Customer Name</label>
          <input 
            type="text" 
            list="customerNames" 
            value={customerName} 
            onChange={handleCustomerChange}
            placeholder="Enter Customer Name" 
            className="read-only-input" 
            disabled={isLoading} 
          />
          <datalist id="customerNames">
            {customerList.map((c, i) => (
              <option key={i} value={c.customerName}>
                {c.customerName} {c.currentBalance !== undefined ? `(Balance: ${c.currentBalance})` : ''}
              </option>
            ))}
          </datalist>
          {selectedCustomerBalance !== null && (
            <div style={{ fontSize: '12px', color: '#0a8938', marginTop: '5px' }}>
              Current Balance: Rs {selectedCustomerBalance.toFixed(2)}
            </div>
          )}
        </div>
      </div>

      {/* Products Table */}
      <div className="table-scroll-wrapper">
        <table className="purchase-table">
          <thead>
            <tr>
              <th>Product Name</th>
              <th>Category</th>
              <th>{showWeightLabel ? "Weight/Quantity" : "Quantity"}</th>
              <th>Rate</th>
              {showSizeColumn && <th>Size</th>}
              {showPacketColumns && (<><th>Ktae</th><th>Gram</th></>)}
              {showBoxColumns && <th>Box Size</th>}
              {showParticularColumn && <th>{hasReelOrTotaOrRoll ? "Vendor Name" : "Particular"}</th>}
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {products.map((product, idx) => {
              const category = product.category.toLowerCase();
              const selectedProduct = productList.find(p => p.productName === product.productName);
              const isReelOrTota = category === 'reel' || category === 'tota';
              const isRoll = category === 'roll';
              const isPacket = category === 'packet';
              
              return (
                <tr key={idx}>
                  <td style={{ position: 'relative', minWidth: '150px', maxWidth: '250px' }}>
                    <input
                      list={`products-${idx}`}
                      value={product.productName}
                      onChange={e => {
                        handleChange(idx, "productName", e.target.value);
                        setSearchTerms(prev => ({
                          ...prev,
                          [idx]: e.target.value
                        }));
                      }}
                      onFocus={() => {
                        setSearchTerms(prev => ({
                          ...prev,
                          [idx]: product.productName || ''
                        }));
                      }}
                      className="read-only-input"
                      placeholder='Search Product...'
                      disabled={isLoading}
                      style={{
                        borderColor: (() => {
                          if (!selectedProduct) return '';
                          const cat = (selectedProduct.category || "").toLowerCase();
                          if ((cat === 'reel' || cat === 'tota' || cat === 'roll') && selectedProduct.stockByWeight) {
                            const sizeVal = product.size || product.length;
                            if (sizeVal) {
                              const hasStock = Object.entries(selectedProduct.stockByWeight).some(([key, qty]) => {
                                const match = key.match(/^(\d+(?:\.\d+)?)/);
                                return match && parseFloat(match[1]) === parseFloat(sizeVal) && qty > 0;
                              });
                              return !hasStock ? '#ff4444' : '';
                            }
                            const hasStock = Object.values(selectedProduct.stockByWeight).some(qty => qty > 0);
                            return !hasStock && product.productName ? '#ff4444' : '';
                          }
                          return (selectedProduct?.stockQuantity !== undefined && selectedProduct.stockQuantity <= 0 && product.productName) ? '#ff4444' : '';
                        })(),
                        backgroundColor: (() => {
                          if (!selectedProduct) return '';
                          const cat = (selectedProduct.category || "").toLowerCase();
                          if ((cat === 'reel' || cat === 'tota' || cat === 'roll') && selectedProduct.stockByWeight) {
                            const sizeVal = product.size || product.length;
                            if (sizeVal) {
                              const hasStock = Object.entries(selectedProduct.stockByWeight).some(([key, qty]) => {
                                const match = key.match(/^(\d+(?:\.\d+)?)/);
                                return match && parseFloat(match[1]) === parseFloat(sizeVal) && qty > 0;
                              });
                              return !hasStock ? '#fff0f0' : '';
                            }
                            const hasStock = Object.values(selectedProduct.stockByWeight).some(qty => qty > 0);
                            return !hasStock && product.productName ? '#fff0f0' : '';
                          }
                          return (selectedProduct?.stockQuantity !== undefined && selectedProduct.stockQuantity <= 0 && product.productName) ? '#fff0f0' : '';
                        })()
                      }}
                    />
                    
                    <datalist id={`products-${idx}`}>
                      {getFilteredProductsForRow(idx, searchTerms[idx] || '', product.size || product.length || '').map((p, i) => {
                        const cat = (p.category || "").toLowerCase();
                        const isReelTotaRoll = cat === 'reel' || cat === 'tota' || cat === 'roll';
                        const isPacket = cat === 'packet';
                        
                        let displayText = p.productName;
                        
                        if (isReelTotaRoll && p.stockByWeight) {
                          const sizeVal = product.size || product.length;
                          if (sizeVal) {
                            const matchingStock = Object.entries(p.stockByWeight).filter(([key, qty]) => {
                              const match = key.match(/^(\d+(?:\.\d+)?)/);
                              return match && parseFloat(match[1]) === parseFloat(sizeVal) && qty > 0;
                            });
                            if (matchingStock.length > 0) {
                              displayText += ` [${matchingStock.map(([key, qty]) => `${key}: ${qty}`).join(', ')}]`;
                            } else {
                              displayText += ' (No stock for this size)';
                            }
                          } else {
                            const nonZero = Object.entries(p.stockByWeight).filter(([_, qty]) => qty > 0);
                            displayText += nonZero.length > 0 ? ` (${nonZero.length} sizes)` : ' (Out)';
                          }
                        } else if (isPacket && p.packetStockByVariant) {
                          const nonZero = Object.entries(p.packetStockByVariant).filter(([_, qty]) => qty > 0);
                          displayText += nonZero.length > 0 ? ` (${nonZero.length} variants)` : ' (Out)';
                        } else if (p.stockQuantity !== undefined) {
                          displayText += p.stockQuantity > 0 ? ` (${p.stockQuantity})` : ' (Out)';
                        }
                        
                        return <option key={i} value={p.productName}>{displayText}</option>;
                      })}
                      {getFilteredProductsForRow(idx, searchTerms[idx] || '', product.size || product.length || '').length === 0 && (
                        <option value="" disabled>
                          {searchTerms[idx] || product.size || product.length 
                            ? 'No products found for this size' 
                            : 'No products available'}
                        </option>
                      )}
                    </datalist>
                    
                    {renderStockDisplay(selectedProduct, product.productName, product.category)}
                    
                    {(product.size || product.length) && (
                      <div style={{ 
                        fontSize: '10px', 
                        color: '#3b82f6', 
                        marginTop: '4px',
                        padding: '2px 6px',
                        backgroundColor: '#e3f2fd',
                        borderRadius: '4px',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}>
                        <span>🔍 Filter: {product.size || product.length}</span>
                        <button 
                          onClick={() => {
                            setSizeFilters(prev => ({...prev, [idx]: ''}));
                            const newProducts = [...products];
                            newProducts[idx].size = '';
                            newProducts[idx].length = '';
                            setProducts(newProducts);
                          }}
                          style={{ 
                            background: 'none', 
                            border: 'none', 
                            color: '#ff4444', 
                            cursor: 'pointer', 
                            fontSize: '12px',
                            fontWeight: 'bold',
                            padding: '0 4px'
                          }}
                          title="Clear size filter"
                        >
                          ✕
                        </button>
                      </div>
                    )}
                    
                    {(category === 'reel' || category === 'tota' || category === 'roll') && (!product.quantity || parseFloat(product.quantity) <= 0) && product.productName && (
                      <div style={{ fontSize: '11px', color: '#ff9800', marginTop: '2px', fontWeight: '500' }}>
                        ⚡ Enter {category === 'roll' ? 'meters/units' : 'weight in kg'} in the Quantity column
                      </div>
                    )}
                    
                    {category === 'roll' && !product.size && product.productName && (
                      <div style={{ fontSize: '11px', color: '#ff4444', marginTop: '2px', fontWeight: '500' }}>
                        ⚠️ Size is required for roll products
                      </div>
                    )}
                    
                    {isReelOrTota && (!product.length || parseFloat(product.length) <= 0) && product.productName && (
                      <div style={{ fontSize: '11px', color: '#ff9800', marginTop: '2px', fontWeight: '500' }}>
                        ⚡ Length is required (e.g., meters)
                      </div>
                    )}
                  </td>

                  <td>
                    <input list={`categories-${idx}`} value={product.category} onChange={e => handleChange(idx, "category", e.target.value)} className="read-only-input" disabled={isLoading} />
                    <datalist id={`categories-${idx}`}>{Array.isArray(categoryList) && categoryList.map((c, i) => <option key={i} value={c.categoryName} />)}</datalist>
                  </td>

                  <td>
                    <input type="number" value={product.quantity} onChange={e => handleChange(idx, "quantity", e.target.value)} className="read-only-input" disabled={isLoading} placeholder={isReelOrTota ? "Weight in kg" : (isRoll ? "Quantity in meters" : "Quantity")} step={isReelOrTota ? "1" : "0.01"} />
                  </td>

                  <td>
                    <input type="number" value={product.price} onChange={e => handleChange(idx, "price", e.target.value)} className="read-only-input" disabled={isLoading} />
                  </td>

                  {/* SINGLE Size column - shows size for packet, roll, and reel/tota */}
{showSizeColumn && (
  <td>
    {/* ---------- INPUT FIELD ---------- */}
    {isPacket || isRoll ? (
      <input 
        type="number" 
        value={product.size || ""} 
        onChange={e => handleChange(idx, "size", e.target.value)} 
        className="read-only-input" 
        disabled={isLoading} 
        placeholder={isRoll ? "Size *" : "Size"}
        style={{ 
          borderColor: (isRoll && !product.size && product.productName) ? '#ff4444' : '',
          fontWeight: isRoll ? '500' : 'normal',
          backgroundColor: (isRoll && !product.size && product.productName) ? '#fff0f0' : ''
        }}
        onWheel={(e) => e.preventDefault()}
      />
    ) : isReelOrTota ? (
      <input 
        type="number" 
        step="any" 
        value={product.length || ""} 
        onChange={e => handleChange(idx, "length", e.target.value)} 
        className="read-only-input" 
        disabled={isLoading} 
        placeholder="Length (m)"
        style={{ 
          borderColor: (!product.length && product.productName) ? '#ff4444' : '',
          fontWeight: '500'
        }}
        onWheel={(e) => e.preventDefault()}
      />
    ) : (
      <input type="text" value="" className="read-only-input" disabled style={{ backgroundColor: '#f0f0f0' }} placeholder="N/A" />
    )}

    {/* ---------- VALIDATION MESSAGES ---------- */}
    {isRoll && !product.size && product.productName && (
      <div style={{ fontSize: '11px', color: '#ff4444', marginTop: '2px', fontWeight: '500' }}>
        ⚠️ Size is required for roll products
      </div>
    )}

    {/* ---------- IMPROVED STOCK DISPLAY (PILLS) ---------- */}
    {selectedProduct && selectedProduct.stockByWeight && (
      <div style={{
        marginTop: '6px',
        display: 'flex',
        flexWrap: 'wrap',
        gap: '4px',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        {(() => {
          let entries = [];
          if (isReelOrTota && product.length) {
            // Reel/Tota: filter by length
            entries = Object.entries(selectedProduct.stockByWeight)
              .filter(([key, qty]) => {
                const match = key.match(/\|\s*(\d+(?:\.\d+)?)m/);
                return match && parseFloat(match[1]) === parseFloat(product.length) && qty > 0;
              });
          } else if (isRoll && product.size) {
            // Roll: filter by size
            entries = Object.entries(selectedProduct.stockByWeight)
              .filter(([key, qty]) => {
                const match = key.match(/^(\d+(?:\.\d+)?)/);
                return match && parseFloat(match[1]) === parseFloat(product.size) && qty > 0;
              });
          }

          if (entries.length === 0) {
            // If a size/length is entered but no stock matches, show "Out of Stock"
            if ((isReelOrTota && product.length) || (isRoll && product.size)) {
              return <span style={{ color: '#ff4444', fontWeight: '500', fontSize: '12px' }}>Out of Stock</span>;
            }
            // Otherwise show nothing (user hasn't entered size/length yet)
            return null;
          }

          // Render each stock entry as a pill
          return entries.map(([display, qty]) => (
            <span key={display} style={{
              background: '#e8f5e9',
              padding: '1px 8px',
              borderRadius: '16px',
              fontSize: '11px',
              fontWeight: '500',
              color: '#1e7e34',
              whiteSpace: 'nowrap',
              border: '1px solid #a5d6a7',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '3px'
            }}>
              <span>{display}</span>
              <strong style={{ fontSize: '12px' }}>{qty}</strong>
            </span>
          ));
        })()}
      </div>
    )}
  </td>
)}

                  {showPacketColumns && (
                    <>
                      <td>{isPacket ? <input type="number" value={product.ktae || ""} onChange={e => handleChange(idx, "ktae", e.target.value)} className="read-only-input" disabled={isLoading} placeholder="Ktae" /> : <input type="text" value="" className="read-only-input" disabled style={{ backgroundColor: '#f0f0f0' }} placeholder="N/A" />}</td>
                      <td>{isPacket ? <input type="number" value={product.gram || ""} onChange={e => handleChange(idx, "gram", e.target.value)} className="read-only-input" disabled={isLoading} placeholder="Gram" /> : <input type="text" value="" className="read-only-input" disabled style={{ backgroundColor: '#f0f0f0' }} placeholder="N/A" />}</td>
                    </>
                  )}

                  {showBoxColumns && (
                    <td>{category === "box" ? <input type="text" value={product.boxSize || ""} onChange={e => handleChange(idx, "boxSize", e.target.value)} className="read-only-input" disabled={isLoading} placeholder="Box Size" /> : <input type="text" value="" className="read-only-input" disabled style={{ backgroundColor: '#f0f0f0' }} placeholder="N/A" />}</td>
                  )}

                  <td>
                    {category !== 'roll' ? (
                      (category === 'reel' || category === 'tota') ? (
                        <>
                          <input 
                            type="text" 
                            list={`vendors-${idx}`} 
                            value={product.particular || ""} 
                            onChange={e => handleChange(idx, "particular", e.target.value)} 
                            className="read-only-input" 
                            placeholder="Select Vendor Name" 
                            disabled={isLoading} 
                            style={{ borderColor: (!product.particular && product.productName) ? '#ff4444' : '' }} 
                          />
                          <datalist id={`vendors-${idx}`}>
                            {vendorList.map(vendor => <option key={vendor.id} value={vendor.vendorName} />)}
                          </datalist>
                          {!product.particular && product.productName && (
                            <div style={{ fontSize: '11px', color: '#ff4444', marginTop: '2px' }}>
                              ⚠️ Vendor name required
                            </div>
                          )}
                          {product.particular && (
                            <div style={{ fontSize: '10px', color: '#0a8938', marginTop: '2px' }}>
                              ✅ Selling from vendor: {product.particular}
                            </div>
                          )}
                        </>
                      ) : (
                        <input 
                          type="text" 
                          value={product.particular || ""} 
                          onChange={e => handleChange(idx, "particular", e.target.value)} 
                          className="read-only-input" 
                          placeholder="Particular" 
                          disabled={isLoading} 
                        />
                      )
                    ) : (
                      <input 
                        type="text" 
                        value="" 
                        className="read-only-input" 
                        disabled 
                        style={{ backgroundColor: '#f0f0f0' }} 
                        placeholder="Not applicable for Roll" 
                      />
                    )}
                  </td>
                  <td>
  <button
    onClick={() => removeRow(idx)}
    className="remove-row-btn"
    title="Remove this row"
  >
    ✕
  </button>
</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: "20px" }}>
        <label style={{ fontWeight: "bold" }}>Description</label>
        <input type="text" value={description} onChange={e => setDescription(e.target.value)} placeholder="Enter Description" className="read-only-input" disabled={isLoading} />
      </div>

      {totalQuantity > 0 && (
        <div style={{ marginTop: "10px", textAlign: "right", fontSize: "16px", fontWeight: "500", color: "#0a8938" }}>
          Total Quantity: {totalQuantity} {products.some(p => {
            const cat = (p.category || "").toLowerCase();
            return cat === "reel" || cat === "tota";
          }) ? "kg" : (products.some(p => (p.category || "").toLowerCase() === "roll") ? "meters" : "units")}
        </div>
      )}

      <div style={{ marginTop: "20px", textAlign: "right", fontSize: "20px", fontWeight: "bold", color: "#0c6303" }}>
        Total Value: Rs {grandTotal}
      </div>

      <div className="table-actions">
        <button onClick={removeAllRows} className="remove-btn" disabled={isLoading}>Remove All</button>
        <button onClick={handleSubmit} className="save-btn" disabled={isLoading} style={{ opacity: isLoading ? 0.6 : 1, cursor: isLoading ? "not-allowed" : "pointer", position: "relative" }}>
          {isLoading ? (<>Saving...</>) : "Save Factory Use"}
        </button>
        <button onClick={addRow} className="add-btn" disabled={isLoading}>Add Product</button>
        <button onClick={handlePrintClick} className="print-btn" disabled={!canPrint || isLoading}>Print Invoice</button>
      </div>

      <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>

      <div style={{ position: "absolute", top: "-9999px" }}><UniversalInvoicePrint ref={printRef} invoice={printInvoice} type="sale" /></div>
    </div>
  );
}

export default FactoryUse;